import React, { useEffect, useState } from 'react';
import SliderTabs from '../shared/SliderTabs';
import { LocationItem } from 'components/types/LocationTypes';
import Icons from 'components/atoms/Icons';
import Modal from 'assets/shared/Modal';
import getOsfViewportSize from 'assets/utils/getOsfViewportSize';

type ProviderSchedulingWidgetProps = {
	providerHeading: string;
	providerEpicId: string;
	locations: Array<LocationItem>;
	deptDays: { [key: string]: string[] };
	providerSchedulable: boolean;
};

const ProviderSchedulingWidget = (props: ProviderSchedulingWidgetProps): JSX.Element => {
	const { providerHeading, providerEpicId, locations, deptDays, providerSchedulable } = props;
	const [providerDeptSchedulable, setProviderDeptSchedulable] = useState(false);
	const [locationsOpen, setLocationsOpen] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState<LocationItem | null>(null);

	const selectLocation = (location: LocationItem) => {
		setSelectedLocation(location);
		document
			.querySelector('.scheduling-widget')
			?.querySelector('.error-msg')
			?.classList.remove('shown');

		deptDays[location?.fields?.EpicDeptId?.value]?.length > 0
			? setProviderDeptSchedulable(true)
			: setProviderDeptSchedulable(false);
		setLocationsOpen(false);
	};

	const showMobileSchedule = () => {
		const showChart = () => {
			document.querySelector('.mychart-container')?.classList.add('shown');
			document.querySelector('.slider-tabs')?.classList.add('hidden');
		};
		!selectedLocation
			? document
					.querySelector('.scheduling-widget')
					?.querySelector('.error-msg')
					?.classList.add('shown')
			: showChart();
	};

	const showDesktopSchedule = () => {
		!selectedLocation &&
			document
				.querySelector('.scheduling-widget')
				?.querySelector('.error-msg')
				?.classList.add('shown');
	};

	useEffect(() => {
		if (typeof window !== 'undefined') {
			window?.addEventListener('resize', function () {
				const viewportSize = getOsfViewportSize();
				(viewportSize == 'lg' || viewportSize == 'xl' || viewportSize == 'xxl') &&
					this.document.querySelector('.slider-tabs')?.classList.remove('hidden');
			});
		}
	}, []);

	return (
		<div className="scheduling-widget">
			<SliderTabs tabsText={['New Patient', 'Returning Patient']}>
				<>
					<div className="location-label-wrapper" role="region">
						<p className="location-label">Location*</p>
						<p className="error-msg" aria-live="polite">
							Select location
						</p>
					</div>
					<div
						className={`${locationsOpen ? 'schedule-location open' : 'schedule-location'}`}
						onClick={() => setLocationsOpen(!locationsOpen)}
					>
						<button className="selector">
							<p className="default">
								{selectedLocation
									? selectedLocation?.fields?.City?.value +
									  ': ' +
									  selectedLocation?.fields?.LocationName?.value
									: 'Choose your location'}
							</p>
							{locationsOpen ? <Icons id="chevron-up" /> : <Icons id="chevron-down" />}
						</button>
						<div className={`${locationsOpen ? 'location-options shown' : 'location-options'}`}>
							{locations?.map((location, index) => {
								return (
									<p
										key={`option-${index}`}
										onClick={() => selectLocation(location)}
										onKeyDown={(e) =>
											e.key == 'Enter' || e.key == ' ' ? selectLocation(location) : ''
										}
										tabIndex={locationsOpen ? 0 : -1}
									>
										{location?.fields?.City?.value + ': ' + location?.fields?.LocationName?.value}
									</p>
								);
							})}
						</div>
					</div>
					<div
						className={`inner-content ${
							!selectedLocation && providerSchedulable
								? 'shown'
								: selectedLocation && providerDeptSchedulable
								? 'shown'
								: 'hidden'
						}`}
						id="online-scheduling"
					>
						<button
							className="btn-primary mobile drawer-btn"
							onClick={() => {
								showMobileSchedule();
							}}
						>
							Schedule Online
						</button>
						{!selectedLocation && providerSchedulable && (
							<button
								className="btn-primary schedule-btn desktop"
								onClick={() => {
									showDesktopSchedule();
								}}
							>
								Schedule Online
							</button>
						)}
					</div>
					<div
						className="inner-content"
						id="call-scheduling"
						style={
							!providerSchedulable
								? { display: 'block' }
								: providerSchedulable && selectedLocation && !providerDeptSchedulable
								? { display: 'block' }
								: { display: 'none' }
						}
					>
						<div className="specialty-scheduling">
							<p className="referral-text">
								Not sure if you need a referral? Contact your insurance company before scheduling to
								see if you need a referral from your primary care physician.
							</p>
							<p className="call-text osf-intro-copy">Call to Schedule</p>
							{locations?.map((location, index) => {
								return (
									<a
										href={`tel:${location?.fields?.Phone?.value}`}
										className={`call-btn btn-primary ${
											(!providerDeptSchedulable &&
												location?.fields?.EpicDeptId?.value ==
													selectedLocation?.fields?.EpicDeptId?.value) ||
											(!selectedLocation && !providerSchedulable && index == 0)
												? 'shown'
												: 'hidden'
										}`}
										key={`phone-${index}`}
									>
										{location?.fields?.Phone?.value}
									</a>
								);
							})}
						</div>
					</div>
					{selectedLocation && providerDeptSchedulable && (
						<Modal
							customClass="scheduling-modal"
							openBtnClass="btn-primary schedule-btn"
							openBtnLabel="Schedule Online"
							portalId="sidebar-modal-root"
						>
							<div className="modal-details">
								<h2>{providerHeading}</h2>
								<p className="subtitle">New Patient Appointment</p>
								<p className="location-label">Location</p>
								<div className="schedule-location">
									<div className="selector">
										<p className="scheduling-modal-location">
											{selectedLocation?.fields?.City?.value +
												': ' +
												selectedLocation?.fields?.LocationName?.value}
										</p>
									</div>
								</div>
							</div>
							<div className="mychart-container">
								<iframe
									className="schedule-frame"
									src={`https://www.osfmychart.org/OSFMyChart/openscheduling/SignupAndSchedule/EmbeddedSchedule?vt=1003&id=${providerEpicId}&dept=${selectedLocation?.fields?.EpicDeptId?.value}&linksource=directory`}
									frameBorder="0"
									allowFullScreen
									tabIndex={0}
								></iframe>
							</div>
						</Modal>
					)}
				</>
				<>
					<div className="inner-content">
						<p className="log-in osf-intro-copy">Log in to OSF MyChart to Schedule</p>
						<a
							href="https://osfmychart.org/OSFMyChart/Authentication/Login?postloginurl=scheduling"
							target="_blank"
							className="mychart-btn btn-primary"
						>
							OSF MyChart
						</a>
					</div>
				</>
			</SliderTabs>
			<div className="mychart-container">
				<iframe
					className="schedule-frame"
					src={`https://www.osfmychart.org/OSFMyChart/openscheduling/SignupAndSchedule/EmbeddedSchedule?vt=1003&id=${providerEpicId}&dept=${selectedLocation?.fields?.EpicDeptId?.value}&linksource=directory`}
					frameBorder="0"
					allowFullScreen
					tabIndex={0}
				></iframe>
				<button
					className="back-btn btn-secondary"
					onClick={() => {
						document.querySelector('.mychart-container')?.classList.remove('shown');
						document.querySelector('.slider-tabs')?.classList.remove('hidden');
					}}
				>
					Back
				</button>
			</div>
			<div id="sidebar-modal-root"></div>
		</div>
	);
};

export default ProviderSchedulingWidget;
